import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { AppContext } from '../context';
import InputField from './layout/InputField';
import Loader from './layout/Loader';
import NoDataFound from './layout/NoDataFound';
import Pagination from './layout/Pagination';
import { PAGE_LIMIT } from '../utils/constant';
import { GET_SMS_NOTES } from '../Pages/company/gql';

interface Props {
  recordId: string;
  noteableType: 'Candidate' | 'Contact';
  noteableName?: string;
}

const getTimezone = (): string => {
  // Extracts the short timezone name (PST, PDT, EST, EDT, etc.)
  // from the user's local browser timezone — reflects DST automatically.
  const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(new Date());
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
};

const formatSmsDateTime = (dateStr: string | Date): string => {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${month}/${day} ${hours}:${minutes} ${ampm} ${getTimezone()}`;
};

const SmsChatView = ({ recordId, noteableType, noteableName }: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const { theme } = useContext(AppContext);
  const isDark = theme === 'dark';

  const [getSmsNotes, { data, loading }] = useLazyQuery(GET_SMS_NOTES);

  const fetchNotes = () => {
    if (recordId) {
      getSmsNotes({
        variables: {
          smsNotesPayload: { noteableId: recordId, noteableType },
          page: currentPage,
          limit,
        },
      });
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line
  }, [recordId, currentPage, limit]);

  const smsNotes = data?.getSmsNotes?.smsNotes ?? [];
  const count = data?.getSmsNotes?.count ?? 0;

  return (
    <Card className="border-0">
      <CardBody className="py-2 mt-2">
        {loading && <Loader loaderClass="sm-height" />}

        {!loading && !smsNotes.length && (
          <div className="empty-table-td">
            <NoDataFound text="No SMS Notes Found!" />
          </div>
        )}

        {!loading && smsNotes.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '12px',
              backgroundColor: isDark ? '#282828' : '#f7f7f7',
              borderRadius: '8px',
              height: '500px',
              overflowY: 'auto',
            }}
          >
            {smsNotes.map((sms: any) => {
              const isOutbound = sms.direction?.toUpperCase() === 'OUTBOUND';
              return (
                <div
                  key={sms.id}
                  style={{
                    display: 'flex',
                    justifyContent: isOutbound ? 'flex-start' : 'flex-end',
                    paddingLeft: isOutbound ? '20px' : '60px',
                    paddingRight: isOutbound ? '60px' : '20px',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '60%',
                      padding: '8px 12px',
                      borderRadius: '12px',
                      backgroundColor: isOutbound
                        ? (isDark ? '#2d2d2d' : '#ffffff')
                        : (isDark ? '#4a3a10' : '#fff4e0'),
                      border: `1px solid rgba(234, 168, 39, 0.35)`,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: '#eaa827',
                        marginBottom: '3px',
                      }}
                    >
                      {isOutbound
                        ? sms.user?.fullName || sms.fromPhoneNumber || 'ATS User'
                        : noteableName || sms.fromPhoneNumber || noteableType}
                      {sms.carrier && (
                        <span style={{ fontWeight: 400, marginLeft: 6, color: isDark ? '#aaa' : '#888' }}>
                          · {sms.carrier}
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: '0.9rem', color: isDark ? '#e9e9e9' : '#33475b', wordBreak: 'break-word', lineHeight: '1.4' }}>
                      {sms.messageContent || '--'}
                    </div>

                    <div style={{ fontSize: '0.68rem', color: isDark ? '#aaa' : '#6c757d', marginTop: '4px', textAlign: 'right' }}>
                      {formatSmsDateTime(sms.smsDeliveryTime || sms.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {smsNotes.length > 0 && (
          <div className="users-pagination mt-3">
            <Pagination
              onPageChange={(pageClicked: number) => setCurrentPage(pageClicked)}
              pageCount={Math.ceil(count / limit)}
              currentPage={currentPage}
            />
            <div className="d-flex align-items-center">
              <div className="pagination-display paginationItemsSelect">
                <p className="m-0">
                  {(currentPage - 1) * limit + 1} to{' '}
                  {Math.min(currentPage * limit, count)} of {count}
                </p>
                <div className="divider"></div>
                <label>Display</label>
                <InputField
                  label=""
                  inputtype="select"
                  selectItems={PAGE_LIMIT.filter((item) => item.value !== limit)}
                  inputid="sms-limit"
                  placeholder={limit.toString()}
                  inputMethod={(value: string) => {
                    setCurrentPage(1);
                    setLimit(parseInt(value));
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default SmsChatView;
